import clsx from "clsx";
import React from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useDrawerContext } from "src/ui/navigation/components/Drawer/Drawer";

import { ListItemButton, ListItemButtonProps, Typography, TypographyProps } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import ListItemIcon from "@mui/material/ListItemIcon";

import { useStyles } from "./NavigationLinks.style";

interface BaseLinkProps extends Omit<ListItemButtonProps<"div">, "title"> {
    title: string;
    condition?: boolean;
    textProps?: TypographyProps;
    startIcon?: string;
    endIcon?: string;
    notCollapsible?: boolean;
    href?: string;
    to?: string;
    target?: string;
    rel?: string;
}
const BaseLink = (props: BaseLinkProps) => {
    const { condition = true, textProps = {}, startIcon, endIcon, className, ...rest } = props;
    const { classes } = useStyles();
    const drawer = useDrawerContext();

    if (!condition) {
        return null;
    }

    if (!drawer.open && !props.notCollapsible) {
        return (
            <div>
                <div className={classes.listItemBox}>
                    <ListItemButton
                        {...rest}
                        aria-label={props.title}
                        aria-current={props.selected ? "page" : undefined}
                        className={clsx(classes.listItem, className)}
                    >
                        <ListItemIcon className={classes.startIcon}>
                            <i className={startIcon} />
                        </ListItemIcon>
                    </ListItemButton>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.listItemBox}>
            <ListItemButton
                {...rest}
                aria-current={props.selected ? "page" : undefined}
                className={clsx(classes.listItem, className)}
            >
                {startIcon ? (
                    <ListItemIcon className={classes.startIcon}>
                        <i className={startIcon} />
                    </ListItemIcon>
                ) : null}
                <Typography className={classes.text} {...textProps}>
                    {props.title}
                </Typography>
                {endIcon ? (
                    <ListItemIcon className={classes.endIcon}>
                        <i className={endIcon} />
                    </ListItemIcon>
                ) : null}
            </ListItemButton>
        </div>
    );
};

interface LegacyLinkProps extends BaseLinkProps {
    href: string;
}
export const LegacyLink = (props: LegacyLinkProps) => {
    const location = useLocation();
    return <BaseLink {...props} selected={location.pathname === props.href} />;
};

interface RouterLinkProps extends BaseLinkProps {
    to: string;
    resetOnReselect?: boolean;
}
export const RouterLink = (props: RouterLinkProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { resetOnReselect, to, ...baseLinkProps } = props;

    const isCurrent = location.pathname === to;

    const handleClick = (event: React.MouseEvent) => {
        if (resetOnReselect && isCurrent) {
            event.preventDefault();
            void navigate(to, { replace: true, state: { reset: Date.now() } });
        }
    };

    return <BaseLink {...baseLinkProps} LinkComponent={Link} to={to} onClick={handleClick} selected={isCurrent} />;
};

interface ExternalLinkProps extends BaseLinkProps {
    url: string;
}
export const ExternalLink = ({ url, ...props }: ExternalLinkProps) => {
    return <BaseLink {...props} href={url} target="_blank" rel="noopener noreferrer" />;
};

type OpenSubmenuContextType = {
    openId: string | null;
    setOpenId: (id: string | null) => void;
};
const OpenSubmenuContext = React.createContext<OpenSubmenuContextType | null>(null);

/**
 * Lets only one submenu inside it be open at a time, so opening one never pushes the rest of the sidebar out of view.
 */
export const SingleOpenSubmenu = ({ children }: React.PropsWithChildren) => {
    const [openId, setOpenId] = React.useState<string | null>(null);
    const value = React.useMemo(() => ({ openId, setOpenId }), [openId]);
    return <OpenSubmenuContext.Provider value={value}>{children}</OpenSubmenuContext.Provider>;
};

export const CollapsibleLink = ({ condition = true, ...props }: React.PropsWithChildren<BaseLinkProps>) => {
    const id = React.useId();
    const group = React.useContext(OpenSubmenuContext);
    const [ownOpen, setOwnOpen] = React.useState(false);
    const drawer = useDrawerContext();

    const open = group ? group.openId === id : ownOpen;
    const setOpen = (value: boolean) => (group ? group.setOpenId(value ? id : null) : setOwnOpen(value));

    React.useEffect(() => {
        if (!drawer.open && open) {
            setOpen(false);
        }
    });

    if (!condition) {
        return null;
    }

    return (
        <div>
            <BaseLink
                {...props}
                aria-expanded={open}
                endIcon={open ? "fa-solid fa-angle-down" : "fa-solid fa-angle-right"}
                onClick={() => {
                    if (!drawer.open) {
                        drawer.setOpen(true);
                    }
                    setOpen(!open);
                }}
            />
            <Collapse in={open} timeout="auto" unmountOnExit style={{ paddingLeft: 10 }}>
                {props.children}
            </Collapse>
        </div>
    );
};
